import React, { useState, useEffect } from "react";
import { API_URL } from "./config"; // Ensure API_URL is imported
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import './TripCards.css'; // Import the CSS for styling
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'; // Import the back arrow icon
import DashboardLayout from './DashboardLayout/DasboardLayout';

const Feedback = () => {
  const APILINK = API_URL;
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'private', 'rideshare'
  const [starFilter, setStarFilter] = useState('all'); // 'all', '5', '4', '3', '2', '1'
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const navigate = useNavigate(); // Initialize useNavigate

  const getFeedback = async () => {
    try {
      setLoading(true);
      // Use new feedback API endpoint to get all feedback
      const response = await fetch(`${APILINK}/feedback/all?limit=50`);
      const data = await response.json();
            
      if (data.success && data.data && data.data.length > 0) {
        // Transform the data to match the expected format
        const transformedData = data.data.map(feedback => ({
          ...feedback,
          rating: feedback.stars_rated,
          feedback: feedback.comment,
          trip_date: feedback.feedback_date,
          rating_direction: feedback.giver_role === 'customer' ? 'customer_to_driver' : 'driver_to_customer',
          rated_by: {
            id: `${feedback.giver_role}-${feedback.trip_id}`,
            name: feedback.giver_role === 'customer' ? 'Customer' : 'Driver'
          },
          rated_for: {
            id: `${feedback.receiver_role}-${feedback.trip_id}`,
            name: feedback.receiver_role === 'customer' ? 'Customer' : 'Driver'
          }
        }));
        
        setFeedbackData(transformedData);
        setError(null);
      } else {
        setFeedbackData([]);
        setError("No feedback data available");
      }
    } catch (error) {
      console.log("Error fetching feedback:", error);
      setError("Failed to fetch feedback data");
      setFeedbackData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFeedback();
    const interval = setInterval(() => {
      getFeedback();
    }, 30000); // 30 seconds instead of 7 seconds
    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={i <= rating ? solidStar : regularStar}
          style={{ color: '#FFD700', marginRight: '4px', fontSize: '20px' }} // Adjust color and size as needed
        />
      );
    }
    return stars;
  };

  return (
    <DashboardLayout>
      <div className="hearder-feed">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#FFD700', // Golden color for the icon
              fontSize: '24px',
              marginRight: '10px' // Space between button and title
            }}
            onClick={() => navigate('/')} // Navigate back to home
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h1 style={{ margin: '0 auto', textAlign: 'center', flexGrow: 1 }}>Recent Feedback</h1>
        </div>

        {/* Loading and Error States */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading feedback data...</p>
          </div>
        )}
        
        {error && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
            <p>{error}</p>
            <button 
              onClick={getFeedback}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Tab Buttons */}
        <div style={{ display: 'flex', marginBottom: '15px', gap: '10px', justifyContent: 'center' }}>
          <button
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'all' ? '#007bff' : '#64748B',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              minWidth: '80px',
              boxShadow: activeTab === 'all' ? '0 2px 8px rgba(0, 123, 255, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onClick={() => setActiveTab('all')}
            onMouseOver={(e) => {
              if (activeTab !== 'all') {
                e.target.style.backgroundColor = '#475569';
              }
            }}
            onMouseOut={(e) => {
              if (activeTab !== 'all') {
                e.target.style.backgroundColor = '#64748B';
              }
            }}
          >
            All ({feedbackData.length})
          </button>
          <button
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'private' ? '#3B82F6' : '#64748B',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              minWidth: '100px',
              boxShadow: activeTab === 'private' ? '0 2px 8px rgba(59, 130, 246, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onClick={() => setActiveTab('private')}
            onMouseOver={(e) => {
              if (activeTab !== 'private') {
                e.target.style.backgroundColor = '#475569';
              }
            }}
            onMouseOut={(e) => {
              if (activeTab !== 'private') {
                e.target.style.backgroundColor = '#64748B';
              }
            }}
          >
            Private ({feedbackData.filter(f => f.trip_type === 'private_trip').length})
          </button>
          <button
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'rideshare' ? '#10B981' : '#64748B',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              minWidth: '100px',
              boxShadow: activeTab === 'rideshare' ? '0 2px 8px rgba(16, 185, 129, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onClick={() => setActiveTab('rideshare')}
            onMouseOver={(e) => {
              if (activeTab !== 'rideshare') {
                e.target.style.backgroundColor = '#475569';
              }
            }}
            onMouseOut={(e) => {
              if (activeTab !== 'rideshare') {
                e.target.style.backgroundColor = '#64748B';
              }
            }}
          >
            Shared ({feedbackData.filter(f => f.trip_type === 'rideshare_request').length})
          </button>
        </div>

        {/* Additional Filters */}
        <div style={{ 
          display: 'flex', 
          marginBottom: '15px', 
          gap: '15px', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Star Rating Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#495057' }}>Stars:</span>
            <select
              value={starFilter}
              onChange={(e) => setStarFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '12px',
                backgroundColor: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Stars</option>
              <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
              <option value="4">⭐⭐⭐⭐ 4 Stars</option>
              <option value="3">⭐⭐⭐ 3 Stars</option>
              <option value="2">⭐⭐ 2 Stars</option>
              <option value="1">⭐ 1 Star</option>
            </select>
          </div>

          {/* Date Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#495057' }}>Date:</span>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '12px',
                backgroundColor: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          <button
            onClick={() => {
              setStarFilter('all');
              setDateFilter('all');
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#6c757d',
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Clear Filters
          </button>
        </div>

        {/* Feedback Data */}
        {!loading && !error && (
          <div>
            <div className="trip-cards-container">
              {(() => {
                // Filter feedback based on active tab, star rating, and date
                let filteredFeedback = activeTab === 'all' 
                  ? feedbackData 
                  : activeTab === 'private' 
                    ? feedbackData.filter(f => f.trip_type === 'private_trip')
                    : feedbackData.filter(f => f.trip_type === 'rideshare_request');

                // Apply star rating filter
                if (starFilter !== 'all') {
                  filteredFeedback = filteredFeedback.filter(f => f.rating === parseInt(starFilter));
                }

                // Apply date filter
                if (dateFilter !== 'all') {
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
                  const yesterdayEnd = new Date(today.getTime() - 1); // End of yesterday (23:59:59)
                  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

                  filteredFeedback = filteredFeedback.filter(f => {
                    const feedbackDate = new Date(f.trip_date);
                    switch (dateFilter) {
                      case 'today':
                        return feedbackDate >= today;
                      case 'yesterday':
                        return feedbackDate >= yesterday && feedbackDate <= yesterdayEnd;
                      case 'week':
                        return feedbackDate >= weekAgo;
                      case 'month':
                        return feedbackDate >= monthAgo;
                      default:
                        return true;
                    }
                  });
                }

                if (filteredFeedback.length > 0) {
                  // Group feedback by trip_id to show customer and driver feedback side by side
                  const groupedFeedback = Object.entries(
                    filteredFeedback.reduce((groups, feedback) => {
                      const tripKey = `${feedback.trip_type}-${feedback.trip_id}`;
                      if (!groups[tripKey]) {
                        groups[tripKey] = {
                          trip_id: feedback.trip_id,
                          trip_type: feedback.trip_type,
                          trip_date: feedback.trip_date,
                          feedback_entries: []
                        };
                      }
                      groups[tripKey].feedback_entries.push(feedback);
                      return groups;
                    }, {})
                  );

                  return groupedFeedback.map(([tripKey, tripData]) => (
                    <div key={tripKey} className="trip-card" style={{
                      marginBottom: '12px',
                      padding: '12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      {/* Compact Header */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: tripData.trip_type === 'private_trip' ? '#3B82F6' : '#10B981',
                            backgroundColor: tripData.trip_type === 'private_trip' ? '#e3f2fd' : '#e8f5e8',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            {tripData.trip_type === 'private_trip' ? '🚗 Private' : '👥 Shared'}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>
                            #{tripData.trip_id}
                          </span>
                        </div>
                        <span style={{ fontSize: '11px', color: '#6c757d' }}>
                          {new Date(tripData.trip_date).toLocaleDateString()}
                        </span>
                      </div>
                      
                      {/* Compact Feedback Display */}
                      <div className="comments-container">
                        <div className="comments-row" style={{ display: 'flex', gap: '10px' }}>
                          {tripData.feedback_entries.map((feedback, index) => (
                            <div key={index} className="comment-section" style={{ flex: 1 }}>
                              <div className="comment-card" style={{
                                backgroundColor: feedback.rating_direction === 'customer_to_driver' ? '#e7f3ff' : '#e8f5e8',
                                padding: '8px',
                                borderRadius: '6px',
                                border: `1px solid ${feedback.rating_direction === 'customer_to_driver' ? '#b3d9ff' : '#c3e6c3'}`
                              }}>
                                {/* Header */}
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  marginBottom: '6px'
                                }}>
                                  <div>
                                    <span style={{ 
                                      fontSize: '11px', 
                                      fontWeight: '600',
                                      color: feedback.rating_direction === 'customer_to_driver' ? '#0056b3' : '#155724'
                                    }}>
                                      {feedback.rating_direction === 'customer_to_driver' ? '👤 Customer' : '🚗 Driver'}
                                    </span>
                                    <span style={{ 
                                      fontSize: '9px', 
                                      color: '#6c757d',
                                      marginLeft: '4px',
                                      backgroundColor: '#f8f9fa',
                                      padding: '1px 4px',
                                      borderRadius: '3px'
                                    }}>
                                      ID: {feedback.rated_by.id || 'N/A'}
                                    </span>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontSize: '10px', color: '#6c757d' }}>
                                      → {feedback.rated_for.name}
                                    </span>
                                    <div style={{ 
                                      fontSize: '9px', 
                                      color: '#6c757d',
                                      marginTop: '1px'
                                    }}>
                                      ID: {feedback.rated_for.id || 'N/A'}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Rating */}
                                <div style={{ marginBottom: '4px' }}>
                                  <span style={{ fontSize: '11px', marginRight: '4px' }}>
                                    {renderStars(feedback.rating)}
                                  </span>
                                  <span style={{ fontSize: '11px', color: '#6c757d' }}>
                                    {feedback.rating}/5
                                  </span>
                                </div>
                                
                                {/* Comment */}
                                <p style={{ 
                                  margin: '0 0 8px 0', 
                                  fontSize: '11px', 
                                  fontStyle: 'italic',
                                  color: '#495057',
                                  lineHeight: '1.3'
                                }}>
                                  "{feedback.feedback || 'No comment'}"
                                </p>

                                {/* Service Feedback Details */}
                                {(feedback.driver_arrived_quickly !== null || 
                                  feedback.price_as_agreed !== null || 
                                  feedback.passenger_felt_safe !== null ||
                                  feedback.car_good_condition !== null ||
                                  feedback.driver_professional !== null ||
                                  feedback.pickup_accurate !== null ||
                                  feedback.passenger_ready_on_time !== null ||
                                  feedback.pickup_location_accurate !== null ||
                                  feedback.passenger_respected_price !== null ||
                                  feedback.passenger_respectful !== null ||
                                  feedback.driver_felt_safe !== null ||
                                  feedback.shared_ride_smooth !== null ||
                                  feedback.route_efficient !== null ||
                                  feedback.satisfied_with_earnings !== null) && (
                                  <div style={{ 
                                    marginTop: '6px', 
                                    padding: '4px', 
                                    backgroundColor: 'rgba(255,255,255,0.5)', 
                                    borderRadius: '3px',
                                    fontSize: '9px'
                                  }}>
                                    <div style={{ fontWeight: '600', marginBottom: '2px', color: '#495057' }}>
                                      Service Details:
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px' }}>
                                      {/* Passenger Service Fields */}
                                      {feedback.giver_role === 'customer' || feedback.giver_role === 'passenger' ? (
                                        <>
                                          {feedback.driver_arrived_quickly !== null && (
                                            <span style={{ color: feedback.driver_arrived_quickly ? '#28a745' : '#dc3545' }}>
                                              {feedback.driver_arrived_quickly ? '✓' : '✗'} Quick Arrival
                                            </span>
                                          )}
                                          {feedback.price_as_agreed !== null && (
                                            <span style={{ color: feedback.price_as_agreed ? '#28a745' : '#dc3545' }}>
                                              {feedback.price_as_agreed ? '✓' : '✗'} Price Agreed
                                            </span>
                                          )}
                                          {feedback.passenger_felt_safe !== null && (
                                            <span style={{ color: feedback.passenger_felt_safe ? '#28a745' : '#dc3545' }}>
                                              {feedback.passenger_felt_safe ? '✓' : '✗'} Felt Safe
                                            </span>
                                          )}
                                          {feedback.car_good_condition !== null && (
                                            <span style={{ color: feedback.car_good_condition ? '#28a745' : '#dc3545' }}>
                                              {feedback.car_good_condition ? '✓' : '✗'} Car Condition
                                            </span>
                                          )}
                                          {feedback.driver_professional !== null && (
                                            <span style={{ color: feedback.driver_professional ? '#28a745' : '#dc3545' }}>
                                              {feedback.driver_professional ? '✓' : '✗'} Professional
                                            </span>
                                          )}
                                          {feedback.pickup_accurate !== null && (
                                            <span style={{ color: feedback.pickup_accurate ? '#28a745' : '#dc3545' }}>
                                              {feedback.pickup_accurate ? '✓' : '✗'} Pickup Accurate
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          {/* Driver Service Fields */}
                                          {feedback.passenger_ready_on_time !== null && (
                                            <span style={{ color: feedback.passenger_ready_on_time ? '#28a745' : '#dc3545' }}>
                                              {feedback.passenger_ready_on_time ? '✓' : '✗'} Ready On Time
                                            </span>
                                          )}
                                          {feedback.pickup_location_accurate !== null && (
                                            <span style={{ color: feedback.pickup_location_accurate ? '#28a745' : '#dc3545' }}>
                                              {feedback.pickup_location_accurate ? '✓' : '✗'} Location Accurate
                                            </span>
                                          )}
                                          {feedback.passenger_respected_price !== null && (
                                            <span style={{ color: feedback.passenger_respected_price ? '#28a745' : '#dc3545' }}>
                                              {feedback.passenger_respected_price ? '✓' : '✗'} Respected Price
                                            </span>
                                          )}
                                          {feedback.passenger_respectful !== null && (
                                            <span style={{ color: feedback.passenger_respectful ? '#28a745' : '#dc3545' }}>
                                              {feedback.passenger_respectful ? '✓' : '✗'} Respectful
                                            </span>
                                          )}
                                          {feedback.driver_felt_safe !== null && (
                                            <span style={{ color: feedback.driver_felt_safe ? '#28a745' : '#dc3545' }}>
                                              {feedback.driver_felt_safe ? '✓' : '✗'} Driver Safe
                                            </span>
                                          )}
                                          {/* Rideshare-specific driver fields */}
                                          {feedback.shared_ride_smooth !== null && (
                                            <span style={{ color: feedback.shared_ride_smooth ? '#28a745' : '#dc3545' }}>
                                              {feedback.shared_ride_smooth ? '✓' : '✗'} Smooth Ride
                                            </span>
                                          )}
                                          {feedback.route_efficient !== null && (
                                            <span style={{ color: feedback.route_efficient ? '#28a745' : '#dc3545' }}>
                                              {feedback.route_efficient ? '✓' : '✗'} Efficient Route
                                            </span>
                                          )}
                                          {feedback.satisfied_with_earnings !== null && (
                                            <span style={{ color: feedback.satisfied_with_earnings ? '#28a745' : '#dc3545' }}>
                                              {feedback.satisfied_with_earnings ? '✓' : '✗'} Satisfied Earnings
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {/* Add empty placeholder if only one feedback entry exists */}
                          {tripData.feedback_entries.length === 1 && (
                            <div className="comment-section" style={{ flex: 1 }}>
                              <div className="comment-card" style={{
                                backgroundColor: '#f8f9fa',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px dashed #dee2e6',
                                textAlign: 'center'
                              }}>
                                <p style={{ 
                                  margin: '0', 
                                  fontSize: '11px', 
                                  color: '#6c757d'
                                }}>
                                  {tripData.feedback_entries[0].rating_direction === 'customer_to_driver' ? '🚗 Driver' : '👤 Customer'} feedback pending
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ));
                } else {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <p style={{ color: '#6c757d', fontSize: '16px' }}>
                        No {activeTab === 'all' ? '' : activeTab + ' '}
                        {starFilter !== 'all' ? starFilter + '-star ' : ''}
                        {dateFilter !== 'all' ? dateFilter + ' ' : ''}
                        feedback data available yet.
                      </p>
                      <p style={{ color: '#6c757d', fontSize: '14px' }}>
                        Try adjusting your filters or check back later for new feedback.
                      </p>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Feedback;